/**
 * RhymeCategorySection - Collapsible category with syllable grouping
 *
 * Displays rhyme category header with collapse/expand, word count, and
 * recommended badge. Organizes words by syllable count within each category.
 *
 * Scope 06: Collects flat words via useWordActions instead of parent-child
 * RhymeWord entries. Collected status derived from words array, not rhymes.
 */

import React, { useState } from 'react'
import { RhymeResultChip } from './RhymeResultChip'
import { useWordActions } from '../../hooks/useWordActions'
import type { RhymeType, SyllableCount } from '../../types/wordCollection'
import type { RhymeCategory, SyllableGroup } from '../../types/wordTools'

// Tooltips from RhymeChip
const CATEGORY_TOOLTIPS: Record<RhymeType, string> = {
  perfect: 'Perfect Rhyme - Identical vowel and consonant endings',
  family: 'Family Rhyme - Similar consonant patterns',
  additive: 'Additive Rhyme - Base word with additional sounds',
  subtractive: 'Subtractive Rhyme - Base word minus sounds',
  assonance: 'Assonance - Same vowel sounds, different consonants',
  consonance: 'Consonance - Same consonant sounds, different vowels'
}

interface RhymeCategorySectionProps {
  /** Category title for display */
  title: string
  /** Rhyme type (for color coding and collection) */
  rhymeType: RhymeType
  /** Category data with syllable groups */
  category: RhymeCategory | SyllableGroup
  /** Short description */
  description: string
  /** Is this category recommended? */
  recommended?: boolean
  /** The word being searched */
  searchWord: string
  /** Optional subcategory (for family rhymes) */
  subcategory?: string
}

/**
 * Collapsible section for a rhyme category.
 *
 * Features:
 * - Collapse/expand toggle
 * - Word count in header
 * - Recommended badge for stability-aware suggestions
 * - Syllable grouping (1, 2, 3, 4+)
 * - Flat-word collection with rhyme_exploration provenance
 */
export const RhymeCategorySection: React.FC<RhymeCategorySectionProps> = ({
  title,
  rhymeType,
  category,
  description,
  recommended = false,
  searchWord,
  subcategory
}) => {
  const [isExpanded, setIsExpanded] = useState(true)

  // Flat-word collection — dedup and reactivation handled by useWordActions
  const { collectWord, uncollectWord, isCollected } = useWordActions()

  // Calculate total word count
  const totalCount =
    category.syllables_1.length +
    category.syllables_2.length +
    category.syllables_3.length +
    category.syllables_4_plus.length

  // Don't render if no words
  if (totalCount === 0) {
    return null
  }

  // Toggle collection for a word — flat word with rhyme_exploration provenance.
  // Store source_word + category intent so the collection grouping engine
  // can co-locate this word and preserve the explorer's classification.
  const handleToggleCollection = (word: string, _syllableCount: SyllableCount) => {
    if (isCollected(word)) {
      uncollectWord(word)
    } else {
      collectWord(word, {
        method: 'rhyme_exploration',
        rhyme_data: {
          source_word: searchWord,
          rhyme_type: rhymeType,
          subcategory,
        },
      })
    }
  }

  return (
    <div
      data-testid={`rhyme-category-${rhymeType}`}
      className="border border-gray-200 rounded-lg p-3"
    >
      {/* Header with toggle */}
      <button
        data-testid={`rhyme-category-toggle-${rhymeType}`}
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left hover:bg-gray-50 rounded px-2 py-1 -mx-2 transition-colors"
        title={CATEGORY_TOOLTIPS[rhymeType]}
        aria-expanded={isExpanded}
        aria-controls={`category-content-${rhymeType}`}
      >
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
            {recommended && (
              <span className="text-xs font-medium px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                ⭐ Recommended
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500">{description}</p>
        </div>

        <div className="flex items-center gap-3 ml-4">
          <span className="text-xs font-medium text-gray-500">
            {totalCount} {totalCount === 1 ? 'word' : 'words'}
          </span>
          <span className="text-gray-400 text-sm">
            {isExpanded ? '▼' : '▶'}
          </span>
        </div>
      </button>

      {/* Syllable groups */}
      {isExpanded && (
        <div
          id={`category-content-${rhymeType}`}
          className="mt-3 space-y-3"
        >
          {category.syllables_1.length > 0 && (
            <SyllableGroupDisplay
              label="1 syllable"
              words={category.syllables_1}
              syllableCount={1}
              rhymeType={rhymeType}
              subcategory={subcategory}
              isWordCollected={isCollected}
              onToggleCollection={handleToggleCollection}
            />
          )}
          {category.syllables_2.length > 0 && (
            <SyllableGroupDisplay
              label="2 syllables"
              words={category.syllables_2}
              syllableCount={2}
              rhymeType={rhymeType}
              subcategory={subcategory}
              isWordCollected={isCollected}
              onToggleCollection={handleToggleCollection}
            />
          )}
          {category.syllables_3.length > 0 && (
            <SyllableGroupDisplay
              label="3 syllables"
              words={category.syllables_3}
              syllableCount={3}
              rhymeType={rhymeType}
              subcategory={subcategory}
              isWordCollected={isCollected}
              onToggleCollection={handleToggleCollection}
            />
          )}
          {category.syllables_4_plus.length > 0 && (
            <SyllableGroupDisplay
              label="4+ syllables"
              words={category.syllables_4_plus}
              syllableCount={4}
              rhymeType={rhymeType}
              subcategory={subcategory}
              isWordCollected={isCollected}
              onToggleCollection={handleToggleCollection}
            />
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Syllable group display with chips
 */
interface SyllableGroupDisplayProps {
  label: string
  words: string[]
  syllableCount: SyllableCount
  rhymeType: RhymeType
  subcategory?: string
  isWordCollected: (word: string) => boolean
  onToggleCollection: (word: string, syllableCount: SyllableCount) => void
}

const SyllableGroupDisplay: React.FC<SyllableGroupDisplayProps> = ({
  label,
  words,
  syllableCount,
  rhymeType,
  subcategory,
  isWordCollected,
  onToggleCollection
}) => {
  return (
    <div data-testid={`rhyme-syllable-group-${syllableCount}`}>
      <div className="text-xs font-medium text-gray-600 mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-2">
        {words.map(word => (
          <RhymeResultChip
            key={`${word}-${syllableCount}`}
            word={word}
            rhymeType={rhymeType}
            syllableCount={syllableCount}
            isCollected={isWordCollected(word)}
            onClick={() => onToggleCollection(word, syllableCount)}
            subcategory={subcategory}
          />
        ))}
      </div>
    </div>
  )
}
